import {
  StepOneEntityEnterprise,
  StepOneEntityProperty,
} from 'entities/registration/multistepForm/properties/stepOneEntity'
import { StepZeroEntity } from 'entities/registration/multistepForm/properties/stepZeroEntity'
import { isEmptyOrDefault } from 'utils/misc'

export const cleanFilters = filters =>
  filters
    .map(f => ({
      ...f,
      items: f.items.filter(i => !!i && !isEmptyOrDefault(i.value, true)),
    }))
    .filter(f => !!f && f.items.length > 0)

export function filterObjectByKeys(source, filter, valueOnlyKeys = []) {
  const filterKeys = Object.keys(filter)
  return Object.keys(source)
    .filter(key => filterKeys.includes(key))
    .reduce((acc, key) => {
      const val = source[key]

      if (valueOnlyKeys.includes(key) && val && typeof val === 'object' && 'value' in val) {
        acc[key] = val.value
      } else {
        acc[key] = val
      }

      return acc
    }, {})
}

export function restorePersistedInput({ options, persisted, setFormValue, field }) {
  if (!persisted) return

  const persistedValue = persisted.value || persisted
  const exists = options.some(opt => opt.value === persistedValue)

  if (exists) {
    setFormValue({ [field]: persistedValue })
  }
}

export function toBase64(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.readAsDataURL(file)
    reader.onload = () => resolve(reader.result)
    reader.onerror = error => reject(error)
  })
}

export function isValidUrl(value) {
  try {
    new URL(value)
    return true
  } catch {
    return false
  }
}

export function extractYouTubeId(url) {
  const regex = /(?:youtube\.com\/(?:[^/]+\/.+\/|(?:v|e(?:mbed)?)\/|shorts\/|.*[?&]v=)|youtu\.be\/)([^"&?/ ]{11})/
  const match = url.match(regex)
  return match ? match[1] : null
}

export function extractVimeoId(url) {
  const regex = /vimeo\.com\/(\d+)/
  const match = url.match(regex)
  return match ? match[1] : null
}

export const getUniqueImageTitle = (originalName, existingImages) => {
  const nameParts = originalName.split('.')
  const extension = nameParts.pop()
  const baseName = nameParts.join('.')

  let title = originalName
  let index = 1

  const exists = existingImages.map(img => img.title?.toLowerCase())

  while (exists.includes(title.toLowerCase())) {
    title = `${baseName} (${index}).${extension}`
    index++
  }

  return title
}

export function calculateStepStatus(entity, validateFn, persistedData = {}) {
  let status = 'not_started'

  if (Object.values(entity).some(v => v)) status = 'in_progress'

  const errors = validateFn(entity, persistedData)
  if (Object.keys(errors).length === 0) status = 'completed'

  return status
}

export function normalizeStepStatuses(steps, statuses) {
  let block = false
  const normalized = {}

  steps.forEach(step => {
    if (block) {
      normalized[step] = statuses[step] === 'completed' ? 'in_progress' : statuses[step]
    } else {
      normalized[step] = statuses[step] || 'not_started'
      if (normalized[step] !== 'completed') {
        block = true
      }
    }
  })

  return normalized
}

export function stripHtml(input) {
  if (typeof input !== 'string') return ''
  const text = input.replace(/<[^>]*>/g, '')
  const textArea = document.createElement('textarea')
  textArea.innerHTML = text
  return textArea.value.trim()
}

export const hasDataBeyondPropertyStepOne = data => {
  if (!data || Object.keys(data).length === 0) return false

  const type = data.announcement_type
  const base =
    type === 'property'
      ? StepOneEntityProperty
      : type === 'enterprise'
      ? StepOneEntityEnterprise
      : StepOneEntityProperty

  const knownKeys = [
    ...Object.keys(StepZeroEntity),
    ...Object.keys(base),
    'property_id',
    'enterprise_id',
  ]

  return Object.keys(data).some(key => !knownKeys.includes(key))
}

export function setNestedValue(obj, path, value) {
  if (!path) return

  const [key, ...rest] = path.split('.')

  if (rest.length === 0) {
    obj[key] = value
    return
  }

  if (!(key in obj) || typeof obj[key] !== 'object' || obj[key] === null) {
    obj[key] = {}
  }

  setNestedValue(obj[key], rest.join('.'), value)
}

export function setNestedValueImmutable(obj, path, value) {
  if (!path) return obj

  const [key, ...rest] = path.split('.')
  const source = obj !== null && typeof obj === 'object' ? obj : {}
  const copy = Array.isArray(source) ? [...source] : { ...source }

  copy[key] = rest.length === 0 ? value : setNestedValueImmutable(source[key], rest.join('.'), value)

  return copy
}

export function getNestedValue(obj, path, defaultValue = undefined) {
  if (!path) return defaultValue

  const [key, ...rest] = path.split('.')

  if (obj === null || typeof obj !== 'object' || !(key in obj)) {
    return defaultValue
  }

  if (rest.length === 0) return obj[key]

  return getNestedValue(obj[key], rest.join('.'), defaultValue)
}

export function isDeepEqual(a, b) {
  if (a === b) return true
  if (typeof a !== 'object' || typeof b !== 'object' || a === null || b === null) return false
  if (Array.isArray(a) !== Array.isArray(b)) return false

  const keysA = Object.keys(a)
  if (keysA.length !== Object.keys(b).length) return false

  return keysA.every(key => isDeepEqual(a[key], b[key]))
}

export const buildChildToParentSlugMap = (allAmenities = []) => {
  const map = new Map()

  allAmenities.forEach(group => {
    const parentSlug = group.parent.slug

    if (group.children) {
      group.children.forEach(child => {
        map.set(child.value, parentSlug)
      })
    }
  })

  return map
}

export const capitalizeWords = str => {
  if (!str) return ''

  return str
    .toLowerCase()
    .split(' ')
    .map(word => {
      return word.charAt(0).toUpperCase() + word.slice(1)
    })
    .join(' ')
}
